import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowRight, Send } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';


export default function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const arrowOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);
  const arrowY = useTransform(scrollYProgress, [0, 0.25], [0, 40]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      {/* Animated Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="blob blob-1 absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#856ae7] opacity-[0.17] blur-[120px]" />
        <div className="blob blob-2 absolute -bottom-40 -right-40 w-[600px] h-[500px] rounded-full bg-[#856ae7] opacity-[0.31] blur-[120px]" />
        <div className="blob blob-3 absolute top-1/3 right-1/4 w-[300px] h-[300px] rounded-full bg-[#92d3ff] opacity-[0.13] blur-[100px]" />
      </div>

      {/* <div 
     className="pointer-events-none absolute inset-0 opacity-[0.8] mix-blend-overlay" 
     style={{backgroundImage: "url('/grain.png')",backgroundRepeat: "repeat",backgroundSize: "300px 300px"}}
     />*/}

      {/* Logo Watermark */}
      <motion.div
        style={{ y, opacity }}
        className="absolute -left-10 -top-20 -translate-y-1/2 opacity-[0.025] pointer-events-none select-none hidden md:block"
        aria-hidden="true"
      >
        <img src={"images/mainBg.png"} alt="" className="w-full h-full opacity-[0.75]" />
      </motion.div>

      {/* Content */}
      <motion.div style={{ y, opacity }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="font-mono text-xl uppercase tracking-[0.3em] text-[#ffffff]/80"
          data-testid="hero-label"
        >
          Not the way a <span className="text-[#ffffff] font-extrabold">human </span>does.
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          className="
    font-syne
    text-4xl
    sm:text-6xl
    md:text-7xl
    lg:text-8xl
    xl:text-9xl
    font-extrabold
    tracking-tighter
    leading-[0.9]
    mb-8
    break-words
  "
          data-testid="hero-title"
        >
          <span className="gradient-text">ZATE</span>
          <span className="text-white">VISS</span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="flex flex-wrap gap-4"
        >
          <Link
            to="/portfolio"
            data-testid="hero-cta-portfolio"
            className="flex items-center gap-2 rounded-full border border-white/15 text-white px-8 py-3.5 hover:bg-white/[0.04] transition-colors duration-200 backdrop-blur-md"
          >
            {t('hero_cta1')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
          <Link
            to="/requests"
            data-testid="hero-cta-brief"
            className="group flex items-center gap-2 rounded-full bg-white text-black font-bold px-8 py-3.5 hover:scale-105 active:scale-95 transition-transform duration-200 shadow-[0_0_30px_rgba(255,255,255,0.12)]"

          >
            <Send className="w-4 h-4" />
            {t('hero_cta2')}
          </Link>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#050505] to-transparent pointer-events-none" />
      <motion.div
        style={{ opacity: arrowOpacity, y: arrowY }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2"
      >
        <ArrowDown className="w-10 h-10 text-white/40 animate-bounce" />
      </motion.div>

    </section>
  );
}
